import type { Selection, TimeValue } from '@heroui/react';
import {
  Form,
  Label,
  Header,
  Switch,
  ListBox,
  Checkbox,
  TextArea,
  TextField,
  TimeField,
  NumberField,
} from '@heroui/react';
import {
  now,
  Time,
  toZoned,
  isToday,
  isSameDay,
  toTimeZone,
  ZonedDateTime,
  parseAbsolute,
  getLocalTimeZone,
  type CalendarDate,
  toCalendarDateTime,
} from '@internationalized/date';
import groupBy from 'lodash.groupby';
import isEqual from 'lodash.isequal';
import pluralize from 'pluralize';
import React from 'react';

import {
  CustomButton,
  SignedImageViewer,
  MetricValuesSection,
} from '@components';
import { useHabits, useTextField } from '@hooks';
import type {
  Habit,
  Occurrence,
  MetricValue,
  OccurrenceFormValues,
} from '@models';
import { StorageBuckets } from '@models';
import { getPublicUrl, getLatestHabitOccurrence } from '@services';
import { isNumericValue, getSupabaseBrowserClient } from '@utils';

import OccurrenceChip from './OccurrenceChip';
import OccurrencePhotosUploader from './OccurrencePhotosUploader';

const ALL_DAY_TIME = { hour: 12, minute: 0 };

type OccurrenceFormViewProps = {
  dayToLog?: CalendarDate;
  habits: Record<string, Habit>;
  isSaving: boolean;
  occurrenceNote?: { content: string; id: string };
  occurrenceToEdit?: Occurrence;
  photoPaths?: string[] | null;
  onClose: () => void;
  onPhotoDelete?: (path: string) => void;
  onSubmit: (values: OccurrenceFormValues) => void;
};

const OccurrenceFormView = ({
  dayToLog,
  habits,
  isSaving,
  occurrenceNote,
  occurrenceToEdit,
  onClose,
  onPhotoDelete,
  onSubmit,
  photoPaths = null,
}: OccurrenceFormViewProps) => {
  const timeZone = getLocalTimeZone();
  const [note, handleNoteChange, clearNote] = useTextField();
  const [selectedHabitId, setSelectedHabitId] = React.useState('');
  const [time, setTime] = React.useState<TimeValue | null>(null);
  const [hasSpecificTime, setHasSpecificTime] = React.useState(true);
  const [isDateTimeInFuture, setIsDateTimeInFuture] = React.useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] =
    React.useState(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
  const [lastOccurredAt, setLastOccurredAt] =
    React.useState<ZonedDateTime | null>(null);
  const [previousMetricValues, setPreviousMetricValues] = React.useState<
    Record<string, MetricValue | undefined>
  >({});
  const [metricValues, setMetricValues] = React.useState<
    Record<string, MetricValue | undefined>
  >({});
  const [autoCompoundedMetricIds, setAutoCompoundedMetricIds] = React.useState<
    Record<string, boolean>
  >({});
  const [stockFilledMetrics, setStockFilledMetrics] = React.useState<
    Record<string, string[]>
  >({});
  const allHabits = useHabits();
  const habitStocks = React.useMemo(() => {
    if (!selectedHabitId || !allHabits[selectedHabitId]) {
      return [];
    }

    return allHabits[selectedHabitId].stocks;
  }, [selectedHabitId, allHabits]);
  const [stockSelections, setStockSelections] = React.useState<
    Record<string, number | null>
  >({});
  const [depletedStockIds, setDepletedStockIds] = React.useState<Set<string>>(
    new Set()
  );

  const existingUsageStockIds = React.useMemo(() => {
    if (!occurrenceToEdit) {
      return new Set<string>();
    }

    return new Set(
      occurrenceToEdit.stockUsages.map((usage) => {
        return usage.habitStockId;
      })
    );
  }, [occurrenceToEdit]);

  // Quantity this occurrence already consumes per stock. When editing, the DB
  // trigger restores this amount before applying the new quantity, so it's
  // available headroom on top of the stock's current remainingItems.
  const existingUsageQuantityByStockId = React.useMemo(() => {
    const map = new Map<string, number>();

    if (!occurrenceToEdit) {
      return map;
    }

    for (const usage of occurrenceToEdit.stockUsages) {
      map.set(usage.habitStockId, usage.quantity ?? 0);
    }

    return map;
  }, [occurrenceToEdit]);

  // remainingItems is depleted by this occurrence's own existing usage, so the
  // true max selectable quantity adds that usage back in.
  const getEffectiveMaxQuantity = React.useCallback(
    (stockId: string, remainingItems: number | null) => {
      if (remainingItems === null) {
        return null;
      }

      return (
        remainingItems + (existingUsageQuantityByStockId.get(stockId) ?? 0)
      );
    },
    [existingUsageQuantityByStockId]
  );

  const activeStocks = React.useMemo(() => {
    return habitStocks.filter((stock) => {
      return !stock.isDepleted || existingUsageStockIds.has(stock.id);
    });
  }, [habitStocks, existingUsageStockIds]);

  const selectedStocks = React.useMemo(() => {
    return activeStocks.filter((stock) => {
      return Object.prototype.hasOwnProperty.call(stockSelections, stock.id);
    });
  }, [activeStocks, stockSelections]);

  const metricDefinitions = React.useMemo(() => {
    if (occurrenceToEdit) {
      return occurrenceToEdit.habit.metricDefinitions;
    }

    if (selectedHabitId && habits[selectedHabitId]) {
      return habits[selectedHabitId].metricDefinitions;
    }

    return [];
  }, [occurrenceToEdit, selectedHabitId, habits]);

  React.useEffect(() => {
    const currentStockIds = new Set(
      selectedStocks.map((stock) => {
        return stock.id;
      })
    );

    // Build a mapping of metricId -> stockIds for the current selection
    const currentMetricSources: Record<string, string[]> = {};

    for (const stock of selectedStocks) {
      for (const metricDefault of stock.metricDefaults) {
        const metricId = metricDefault.habitMetricId;

        if (!currentMetricSources[metricId]) {
          currentMetricSources[metricId] = [];
        }

        currentMetricSources[metricId].push(stock.id);
      }
    }

    // Find orphaned metrics: previously auto-filled by stocks that are now all deselected
    const orphanedMetricIds = new Set<string>();

    for (const [metricId, sourceStockIds] of Object.entries(
      stockFilledMetrics
    )) {
      const hasRemainingSource = sourceStockIds.some((stockId) => {
        return currentStockIds.has(stockId);
      });

      if (!hasRemainingSource) {
        orphanedMetricIds.add(metricId);
      }
    }

    if (selectedStocks.length === 0) {
      setAutoCompoundedMetricIds((prev) => {
        return Object.keys(prev).length === 0 ? prev : {};
      });

      if (orphanedMetricIds.size > 0) {
        setMetricValues((prev) => {
          const next = { ...prev };
          let hasChanges = false;

          for (const metricId of orphanedMetricIds) {
            if (metricId in next) {
              delete next[metricId];
              hasChanges = true;
            }
          }

          return hasChanges ? next : prev;
        });

        setStockFilledMetrics({});
      }

      return;
    }

    const compoundSums: Record<string, number> = {};
    const compoundMetricIds = new Set<string>();

    for (const stock of selectedStocks) {
      const quantity = stockSelections[stock.id] ?? 1;

      for (const metricDefault of stock.metricDefaults) {
        if (!metricDefault.shouldCompound) {
          continue;
        }

        const metricDefinition = metricDefinitions.find((definition) => {
          return definition.id === metricDefault.habitMetricId;
        });

        if (metricDefinition?.type !== 'number') {
          continue;
        }

        if (!isNumericValue(metricDefault.value)) {
          continue;
        }

        compoundMetricIds.add(metricDefault.habitMetricId);
        compoundSums[metricDefault.habitMetricId] =
          (compoundSums[metricDefault.habitMetricId] || 0) +
          metricDefault.value.numericValue * quantity;
      }
    }

    setMetricValues((prev) => {
      let hasChanges = false;
      const next = { ...prev };

      // Remove orphaned metrics that the user hasn't manually edited
      for (const metricId of orphanedMetricIds) {
        if (metricId in next && !compoundMetricIds.has(metricId)) {
          delete next[metricId];
          hasChanges = true;
        }
      }

      for (const stock of selectedStocks) {
        for (const metricDefault of stock.metricDefaults) {
          const metricId = metricDefault.habitMetricId;

          if (metricDefault.shouldCompound && compoundMetricIds.has(metricId)) {
            const shouldOverride =
              prev[metricId] === undefined || autoCompoundedMetricIds[metricId];

            if (shouldOverride) {
              next[metricId] = {
                numericValue: compoundSums[metricId] ?? 0,
              };
              hasChanges = true;
            }

            continue;
          }

          if (next[metricId] === undefined) {
            next[metricId] = metricDefault.value;
            hasChanges = true;
          }
        }
      }

      return hasChanges ? next : prev;
    });

    setAutoCompoundedMetricIds((prev) => {
      const prevKeys = Object.keys(prev);

      if (
        prevKeys.length === compoundMetricIds.size &&
        prevKeys.every((key) => {
          return compoundMetricIds.has(key);
        })
      ) {
        return prev;
      }

      const next: Record<string, boolean> = {};

      for (const metricId of compoundMetricIds) {
        next[metricId] = true;
      }

      return next;
    });

    setStockFilledMetrics((prev) => {
      return isEqual(prev, currentMetricSources) ? prev : currentMetricSources;
    });
  }, [
    selectedStocks,
    stockSelections,
    metricDefinitions,
    autoCompoundedMetricIds,
    stockFilledMetrics,
  ]);

  const computeOccurrenceDateTime = React.useCallback(
    (tz = timeZone) => {
      const baseNow = now(tz);

      const getTimeValues = () => {
        if (time) {
          return { hour: time.hour, minute: time.minute };
        }

        return { hour: baseNow.hour, minute: baseNow.minute };
      };

      if (dayToLog) {
        const timeToSet = hasSpecificTime ? getTimeValues() : ALL_DAY_TIME;

        return toZoned(toCalendarDateTime(dayToLog).set(timeToSet), tz);
      }

      if (hasSpecificTime) {
        const base = occurrenceToEdit?.occurredAt ?? baseNow;
        const { hour, minute } = getTimeValues();

        return toTimeZone(base.set({ hour, minute }), tz);
      }

      const baseDateTime = occurrenceToEdit?.occurredAt ?? baseNow;

      return toZoned(toCalendarDateTime(baseDateTime).set(ALL_DAY_TIME), tz);
    },
    [dayToLog, occurrenceToEdit?.occurredAt, hasSpecificTime, time, timeZone]
  );

  const habitsByTraitName = React.useMemo(() => {
    return groupBy(Object.values(habits), (habit) => {
      return habit.trait?.name || 'Unknown';
    });
  }, [habits]);

  const hasHabits = Object.keys(habits).length > 0;

  React.useEffect(() => {
    if (occurrenceToEdit) {
      return;
    }

    if (!selectedHabitId || !dayToLog) {
      setLastOccurredAt(null);
      setPreviousMetricValues({});

      return;
    }

    getLatestHabitOccurrence(getSupabaseBrowserClient(), selectedHabitId)
      .then((occurrence) => {
        if (!occurrence) {
          setLastOccurredAt(null);
          setPreviousMetricValues({});

          return;
        }

        const lastOccurrenceZdt = parseAbsolute(
          occurrence.occurredAt,
          timeZone
        );

        if (dayToLog.compare(lastOccurrenceZdt) < 0) {
          setLastOccurredAt(null);
          setPreviousMetricValues({});

          return;
        }

        setLastOccurredAt(lastOccurrenceZdt);

        const prevMetrics: Record<string, MetricValue | undefined> = {};

        for (const mv of occurrence.metricValues) {
          prevMetrics[mv.habitMetricId] = mv.value;
        }

        setPreviousMetricValues(prevMetrics);
      })
      .catch(() => {
        setLastOccurredAt(null);
        setPreviousMetricValues({});
      });
  }, [occurrenceToEdit, selectedHabitId, dayToLog, timeZone]);

  React.useEffect(() => {
    if (!dayToLog && !occurrenceToEdit) {
      return;
    }

    if (occurrenceToEdit) {
      setSelectedHabitId(occurrenceToEdit.habitId);
      handleNoteChange(occurrenceNote?.content || '');
      setTime(occurrenceToEdit.occurredAt);
      setHasSpecificTime(occurrenceToEdit.hasSpecificTime);
      setDepletedStockIds(new Set());
      setAutoCompoundedMetricIds({});
      setStockFilledMetrics({});

      const initialStockSelections: Record<string, number | null> = {};

      for (const usage of occurrenceToEdit.stockUsages) {
        initialStockSelections[usage.habitStockId] = usage.quantity;
      }

      setStockSelections(initialStockSelections);

      const initialMetricValues: Record<string, MetricValue | undefined> = {};

      for (const mv of occurrenceToEdit.metricValues) {
        initialMetricValues[mv.habitMetricId] = mv.value;
      }

      setMetricValues(initialMetricValues);

      return;
    }

    if (dayToLog) {
      const { hour, minute } = now(timeZone);

      setTime(new Time(hour, minute, 0));
    }
  }, [
    dayToLog,
    occurrenceNote?.content,
    occurrenceToEdit,
    handleNoteChange,
    timeZone,
  ]);

  React.useEffect(() => {
    if (!Object.keys(habits).length) {
      return;
    }

    if (dayToLog) {
      setIsSubmitButtonDisabled(!selectedHabitId);

      return;
    }

    if (occurrenceToEdit) {
      const hasTimeChanged =
        time instanceof ZonedDateTime &&
        !!time.compare(occurrenceToEdit.occurredAt);
      const hasNoteChanged = note !== (occurrenceNote?.content || '');
      const hasSpecificTimeChanged =
        hasSpecificTime !== occurrenceToEdit.hasSpecificTime;

      const hasMetricValuesChanged = (() => {
        const existingMetricValues: Record<string, MetricValue> = {};

        for (const mv of occurrenceToEdit.metricValues) {
          existingMetricValues[mv.habitMetricId] = mv.value;
        }

        const allMetricIds = new Set<string>([
          ...Object.keys(existingMetricValues),
          ...Object.keys(metricValues),
        ]);

        for (const metricId of allMetricIds) {
          const existingValue = existingMetricValues[metricId];
          const newValue = metricValues[metricId];

          if (!isEqual(existingValue, newValue)) {
            return true;
          }
        }

        return false;
      })();

      const hasStockUsagesChanged = (() => {
        const existingUsages: Record<string, number | null> = {};

        for (const usage of occurrenceToEdit.stockUsages) {
          existingUsages[usage.habitStockId] = usage.quantity;
        }

        return !isEqual(existingUsages, stockSelections);
      })();

      const hasOccurrenceChanged =
        hasNoteChanged ||
        hasTimeChanged ||
        hasSpecificTimeChanged ||
        hasMetricValuesChanged ||
        hasStockUsagesChanged ||
        depletedStockIds.size > 0 ||
        uploadedFiles.length > 0;

      setIsSubmitButtonDisabled(
        isSaving || !selectedHabitId || !hasOccurrenceChanged
      );

      return;
    }
  }, [
    dayToLog,
    occurrenceToEdit,
    occurrenceNote?.content,
    uploadedFiles.length,
    note,
    selectedHabitId,
    time,
    hasSpecificTime,
    isSaving,
    habits,
    metricValues,
    stockSelections,
    depletedStockIds.size,
  ]);

  React.useEffect(() => {
    if (!time) {
      return;
    }

    if (dayToLog) {
      const userEnteredDateTime = toCalendarDateTime(dayToLog).set({
        hour: time.hour,
        minute: time.minute,
      });

      setIsDateTimeInFuture(
        toZoned(userEnteredDateTime, timeZone).compare(now(timeZone)) > 0
      );

      return;
    }

    if (occurrenceToEdit) {
      setIsDateTimeInFuture(
        occurrenceToEdit.occurredAt
          .set({
            hour: time.hour,
            minute: time.minute,
          })
          .compare(now(timeZone)) > 0
      );
    }
  }, [dayToLog, occurrenceToEdit, time, timeZone]);

  const handleSubmit = () => {
    if (!(dayToLog || occurrenceToEdit) || !hasHabits || !selectedHabitId) {
      return;
    }

    const hasExcessiveQuantity = selectedStocks.some((stock) => {
      const quantity = stockSelections[stock.id];
      const effectiveMax = getEffectiveMaxQuantity(
        stock.id,
        stock.remainingItems
      );

      return (
        quantity !== null && effectiveMax !== null && quantity > effectiveMax
      );
    });

    if (hasExcessiveQuantity) {
      return;
    }

    const effectiveTimeZone = occurrenceToEdit?.timeZone ?? timeZone;
    const occurredAt =
      computeOccurrenceDateTime(effectiveTimeZone).toAbsoluteString();
    const stockUsages = Object.entries(stockSelections).map(
      ([habitStockId, quantity]) => {
        return {
          habitStockId,
          quantity: quantity ?? null,
        };
      }
    );

    onSubmit({
      depletedStockIds: [...depletedStockIds],
      hasSpecificTime,
      metricValues,
      note,
      occurredAt,
      selectedHabitId,
      stockUsages,
      uploadedFiles,
    });
  };

  const handleClose = () => {
    setSelectedHabitId('');
    clearNote();
    setUploadedFiles([]);
    setMetricValues({});
    setHasSpecificTime(true);
    setStockSelections({});
    setDepletedStockIds(new Set());
    setAutoCompoundedMetricIds({});
    setStockFilledMetrics({});
    onClose();
  };

  const handleHabitSelectionChange = ([id]: Selection) => {
    if (typeof id === 'string' && id) {
      setSelectedHabitId(id);
      setMetricValues({});
      setStockSelections({});
      setDepletedStockIds(new Set());
      setAutoCompoundedMetricIds({});
      setStockFilledMetrics({});
    }
  };

  const handleStockSelectionChange = (
    stockId: string,
    isSelected: boolean,
    defaultQuantity: number | null
  ) => {
    setStockSelections((prev) => {
      const next = { ...prev };

      if (isSelected) {
        next[stockId] = prev[stockId] ?? defaultQuantity;
      } else {
        delete next[stockId];
      }

      return next;
    });

    if (!isSelected) {
      setDepletedStockIds((prev) => {
        if (!prev.has(stockId)) {
          return prev;
        }

        const next = new Set(prev);

        next.delete(stockId);

        return next;
      });
    }
  };

  const handleStockQuantityChange = (
    stockId: string,
    quantity: number | null,
    maxQuantity: number | null
  ) => {
    const clamped =
      quantity !== null && maxQuantity !== null
        ? Math.min(quantity, maxQuantity)
        : quantity;

    setStockSelections((prev) => {
      return { ...prev, [stockId]: clamped };
    });
  };

  const formatDistanceToNow = (date: ZonedDateTime) => {
    const diffMs = now(timeZone).toDate().getTime() - date.toDate().getTime();

    const isYesterday = isSameDay(date, now(timeZone).subtract({ days: 1 }));

    if (isYesterday || isToday(date, timeZone)) {
      const hoursDifference = Math.floor(diffMs / (1000 * 60 * 60));

      if (hoursDifference < 1) {
        return 'less than an hour';
      }

      return `${hoursDifference} ${pluralize('hour', hoursDifference)}`;
    }

    const daysDifference = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    return `${pluralize('day', daysDifference, true)}`;
  };

  return (
    <Form className="space-y-3 pb-10">
      {occurrenceToEdit ? (
        <OccurrenceChip
          isHabitNameShown
          hasMargin={false}
          hasCounter={false}
          hasTooltip={false}
          isClickable={false}
          occurrences={[occurrenceToEdit]}
        />
      ) : hasHabits ? (
        <>
          <div className="bg-background-secondary max-h-100 w-full overflow-y-auto rounded-3xl p-1">
            <ListBox
              aria-label="Habits"
              disallowEmptySelection
              selectionMode="single"
              onSelectionChange={handleHabitSelectionChange}
              selectedKeys={new Set(selectedHabitId ? [selectedHabitId] : [])}
            >
              {Object.entries(habitsByTraitName).map(([traitName, habits]) => {
                if (!habits?.length) {
                  return null;
                }

                return (
                  <ListBox.Section key={traitName}>
                    <Header className="bg-background shadow-small rounded-small sticky top-1 z-20 mb-1 flex w-full rounded-2xl px-2 py-1.5">
                      {traitName}
                    </Header>
                    {habits.map((habit) => {
                      return (
                        <ListBox.Item
                          id={habit.id}
                          key={habit.id}
                          textValue={habit.name}
                          className="hover:bg-accent-soft-hover relative z-10 border-2 border-transparent"
                        >
                          <Label>
                            <div className="flex items-center gap-2">
                              <img
                                alt={habit.name}
                                className="h-4 w-4"
                                src={getPublicUrl(
                                  StorageBuckets.HABIT_ICONS,
                                  habit.iconPath
                                )}
                              />
                              <span>{habit.name}</span>
                            </div>
                          </Label>
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      );
                    })}
                  </ListBox.Section>
                );
              })}
            </ListBox>
          </div>
          <p className="text-tiny text-foreground-400">
            {lastOccurredAt
              ? `Last logged ${formatDistanceToNow(lastOccurredAt)} ago`
              : 'Choose your habit'}
          </p>
        </>
      ) : (
        <p className="text-foreground-500 text-sm">
          No habits yet. Create a habit to get started.
        </p>
      )}
      {selectedHabitId && activeStocks.length > 0 && (
        <div className="space-y-2">
          <div>
            <p className="text-foreground-500 text-tiny font-medium">
              Stock usage
            </p>
            <p className="text-foreground-400 text-tiny">
              Select a stock to auto-populate metric values.
            </p>
          </div>
          <div className="space-y-2">
            {activeStocks.map((stock) => {
              const isSelected = Object.prototype.hasOwnProperty.call(
                stockSelections,
                stock.id
              );
              const isQuantifiable = stock.totalItems !== null;
              const effectiveMaxQuantity = getEffectiveMaxQuantity(
                stock.id,
                stock.remainingItems
              );

              return (
                <div key={stock.id} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <Checkbox
                      variant="secondary"
                      isSelected={isSelected}
                      onChange={(nextSelected: boolean) => {
                        return handleStockSelectionChange(
                          stock.id,
                          nextSelected,
                          isQuantifiable ? 1 : null
                        );
                      }}
                    >
                      <Checkbox.Content>
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <Label className="text-sm">{stock.name}</Label>
                      </Checkbox.Content>
                    </Checkbox>
                    {isQuantifiable && (
                      <NumberField
                        minValue={1}
                        className="w-28"
                        isDisabled={!isSelected}
                        aria-label={`${stock.name} quantity`}
                        value={stockSelections[stock.id] ?? 1}
                        maxValue={effectiveMaxQuantity ?? undefined}
                        onChange={(value: number) => {
                          return handleStockQuantityChange(
                            stock.id,
                            value ?? 1,
                            effectiveMaxQuantity
                          );
                        }}
                      >
                        <NumberField.Group>
                          <NumberField.DecrementButton />
                          <NumberField.Input />
                          <NumberField.IncrementButton />
                        </NumberField.Group>
                      </NumberField>
                    )}
                  </div>
                  {isSelected && !isQuantifiable && (
                    <Checkbox
                      className="pl-7"
                      isSelected={depletedStockIds.has(stock.id)}
                      onChange={(checked: boolean) => {
                        setDepletedStockIds((prev) => {
                          const next = new Set(prev);

                          if (checked) {
                            next.add(stock.id);
                          } else {
                            next.delete(stock.id);
                          }

                          return next;
                        });
                      }}
                    >
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <Checkbox.Content>
                        <Label className="text-foreground-400 text-tiny">
                          Mark as depleted
                        </Label>
                      </Checkbox.Content>
                    </Checkbox>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <MetricValuesSection
        values={metricValues}
        metricDefinitions={metricDefinitions}
        previousValues={previousMetricValues}
        onChange={(nextValues) => {
          setMetricValues(nextValues);

          const editedMetricIds: string[] = [];

          setAutoCompoundedMetricIds((prev) => {
            const next = { ...prev };

            for (const metricId of Object.keys(next)) {
              const prevValue = metricValues[metricId];
              const nextValue = nextValues[metricId];

              if (!isEqual(prevValue, nextValue)) {
                delete next[metricId];
                editedMetricIds.push(metricId);
              }
            }

            return next;
          });

          for (const metricId of Object.keys(stockFilledMetrics)) {
            if (editedMetricIds.includes(metricId)) {
              continue;
            }

            const prevValue = metricValues[metricId];
            const nextValue = nextValues[metricId];

            if (!isEqual(prevValue, nextValue)) {
              editedMetricIds.push(metricId);
            }
          }

          if (editedMetricIds.length > 0) {
            setStockFilledMetrics((prev) => {
              const next = { ...prev };

              for (const metricId of editedMetricIds) {
                delete next[metricId];
              }

              return next;
            });
          }
        }}
      />
      <TextField value={note} variant="secondary" onChange={handleNoteChange}>
        <Label>Note</Label>
        <TextArea
          fullWidth
          placeholder="Note"
          className="occurrence-note-textarea field-sizing-content max-h-32 min-h-16 max-lg:text-base"
        />
      </TextField>
      <div className="w-full space-y-2">
        <Switch
          className="basis-full"
          isSelected={hasSpecificTime}
          onChange={setHasSpecificTime}
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Label>Specify time</Label>
          </Switch.Content>
        </Switch>
        {hasSpecificTime && (
          <TimeField fullWidth name="time" value={time} onChange={setTime}>
            <Label>Time</Label>
            <TimeField.Group variant="secondary">
              <TimeField.Input>
                {(segment) => {
                  return <TimeField.Segment segment={segment} />;
                }}
              </TimeField.Input>
            </TimeField.Group>
          </TimeField>
        )}
        {isDateTimeInFuture && hasSpecificTime && (
          <p className="text-sm text-gray-600">
            You are logging a habit for the future. Are you a time traveler?
          </p>
        )}
      </div>
      <OccurrencePhotosUploader
        files={uploadedFiles}
        photoPaths={photoPaths}
        onFilesChange={setUploadedFiles}
      />
      {onPhotoDelete && (
        <SignedImageViewer
          paths={photoPaths}
          onDelete={onPhotoDelete}
          bucket={StorageBuckets.OCCURRENCE_PHOTOS}
        />
      )}
      <div className="bg-background dark:bg-surface-tertiary border-border-tertiary fixed right-0 bottom-0 flex w-full items-center justify-between gap-2 border-t p-4">
        {hasHabits ? (
          <CustomButton
            fullWidth
            variant="primary"
            isPending={isSaving}
            onPress={handleSubmit}
            isDisabled={isSubmitButtonDisabled}
          >
            {occurrenceToEdit ? 'Update' : 'Add'}
          </CustomButton>
        ) : (
          <CustomButton
            href="/habits"
            variant="primary"
            className="w-full"
            onPress={handleClose}
            isDisabled={isSubmitButtonDisabled}
          >
            Go to Habits
          </CustomButton>
        )}
      </div>
    </Form>
  );
};

export default OccurrenceFormView;
